import React from 'react';
import { Paper, Box, Typography, Skeleton, ToggleButtonGroup, ToggleButton } from '@mui/material';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';
import { formatDate } from '../../utils/dashboardHelpers';
import ShowChartIcon from '@mui/icons-material/ShowChart';
import BarChartIcon from '@mui/icons-material/BarChart';

/**
 * Gráfico de servicios por día
 * Muestra la evolución de servicios completados y cancelados
 * Permite alternar entre vista de líneas y barras
 */
const ServiciosPorDiaChart = ({ data, loading = false }) => {
  const [chartType, setChartType] = React.useState('line');

  const handleChartTypeChange = (event, newType) => {
    if (newType !== null) {
      setChartType(newType);
    }
  };

  if (loading) {
    return (
      <Paper
        elevation={0}
        sx={{
          p: 3,
          background: 'linear-gradient(135deg, #ffffff 0%, #f5f5f5 100%)',
          border: '1px solid rgba(0, 0, 0, 0.08)',
          borderRadius: 2,
          height: 400
        }}
      >
        <Skeleton variant="text" width="60%" height={40} />
        <Skeleton variant="rectangular" width="100%" height={300} sx={{ mt: 2, borderRadius: 1 }} />
      </Paper>
    );
  }

  if (!data || data.length === 0) {
    return (
      <Paper
        elevation={0}
        sx={{
          p: 3,
          background: 'linear-gradient(135deg, #ffffff 0%, #f5f5f5 100%)',
          border: '1px solid rgba(0, 0, 0, 0.08)',
          borderRadius: 2,
          height: 400,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}
      >
        <Typography color="text.secondary">No hay datos disponibles</Typography>
      </Paper>
    );
  }

  // Formatear datos para el gráfico
  const chartData = data.map(item => ({
    fecha: formatDate(item.fecha, 'dd MMM'),
    Completados: item.completados || 0,
    Cancelados: item.cancelados || 0,
    Total: item.cantidad || 0
  }));

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <Paper
          elevation={3}
          sx={{
            p: 1.5,
            bgcolor: 'rgba(255, 255, 255, 0.98)',
            border: '1px solid rgba(0, 0, 0, 0.1)'
          }}
        >
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
            {label}
          </Typography>
          {payload.map((entry, index) => (
            <Typography
              key={index}
              variant="body2"
              sx={{ color: entry.color, fontSize: '0.875rem' }}
            >
              {entry.name}: {entry.value}
            </Typography>
          ))}
        </Paper>
      );
    }
    return null;
  };

  return (
    <Paper
      elevation={0}
      sx={{
        p: 3,
        background: 'linear-gradient(135deg, #ffffff 0%, #f5f5f5 100%)',
        border: '1px solid rgba(0, 0, 0, 0.08)',
        borderRadius: 2,
        height: 400
      }}
    >
      {/* Header con título y selector de tipo */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography
          variant="h6"
          sx={{
            fontWeight: 600,
            color: '#1A237E',
            fontSize: '1.125rem'
          }}
        >
          📊 Servicios por Día
        </Typography>

        <ToggleButtonGroup
          value={chartType}
          exclusive
          onChange={handleChartTypeChange}
          size="small"
          sx={{
            '& .MuiToggleButton-root': {
              border: '1px solid rgba(26, 35, 126, 0.2)',
              color: '#1A237E',
              '&.Mui-selected': {
                bgcolor: '#1A237E',
                color: 'white',
                '&:hover': {
                  bgcolor: '#3949AB'
                }
              }
            }
          }}
        >
          <ToggleButton value="line" aria-label="línea">
            <ShowChartIcon fontSize="small" />
          </ToggleButton>
          <ToggleButton value="bar" aria-label="barra">
            <BarChartIcon fontSize="small" />
          </ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {/* Gráfico */}
      <ResponsiveContainer width="100%" height={300}>
        {chartType === 'line' ? (
          <LineChart data={chartData} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
            <XAxis
              dataKey="fecha"
              stroke="#666"
              style={{ fontSize: '0.75rem' }}
            />
            <YAxis
              stroke="#666"
              style={{ fontSize: '0.75rem' }}
            />
            <Tooltip content={<CustomTooltip />} />
            <Legend
              wrapperStyle={{ fontSize: '0.875rem' }}
              iconType="circle"
            />
            <Line
              type="monotone"
              dataKey="Completados"
              stroke="#4CAF50"
              strokeWidth={2}
              dot={{ fill: '#4CAF50', r: 4 }}
              activeDot={{ r: 6 }}
            />
            <Line
              type="monotone"
              dataKey="Cancelados"
              stroke="#F44336"
              strokeWidth={2}
              dot={{ fill: '#F44336', r: 4 }}
              activeDot={{ r: 6 }}
            />
          </LineChart>
        ) : (
          <BarChart data={chartData} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
            <XAxis
              dataKey="fecha"
              stroke="#666"
              style={{ fontSize: '0.75rem' }}
            />
            <YAxis
              stroke="#666"
              style={{ fontSize: '0.75rem' }}
            />
            <Tooltip content={<CustomTooltip />} />
            <Legend
              wrapperStyle={{ fontSize: '0.875rem' }}
              iconType="circle"
            />
            <Bar dataKey="Completados" fill="#4CAF50" radius={[4, 4, 0, 0]} />
            <Bar dataKey="Cancelados" fill="#F44336" radius={[4, 4, 0, 0]} />
          </BarChart>
        )}
      </ResponsiveContainer>
    </Paper>
  );
};

export default ServiciosPorDiaChart;
